import apiClient from "./apiClient";

// =====================================================
// Types — match backend contracts (spec AC-28..30)
// =====================================================

/**
 * Client source — mirrors backend `ClientSource` enum — keep these in sync.
 */
export type ClientSource = "inbound" | "outbound";

/**
 * Filter variant that extends ClientSource with 'none' for IS NULL filtering.
 * Mirrors backend `ClientSourceFilter` — keep these in sync.
 */
export type ClientSourceFilter = "inbound" | "outbound" | "none";

/** Single source of Polish labels for client source values — used in lists and forms. */
export const CLIENT_SOURCE_LABELS: Record<ClientSource | "none", string> = {
  inbound: "Inbound",
  outbound: "Outbound",
  none: "brak",
};

/**
 * Marketing filter for the admin tenants list — mirrors backend
 * `GET /admin/tenants?marketing=` (AC-10). Newsletter consent exists only
 * since 2026-06-25, hence the "no_consent" label wording.
 */
export type MarketingFilter = "sendable" | "opted_out" | "no_consent";

export const MARKETING_FILTER_LABELS: Record<MarketingFilter, string> = {
  sendable: "Można wysyłać",
  opted_out: "NIE WYSYŁAĆ",
  no_consent: "Brak zgody / nie pytano",
};

export interface AdminTenantListItem {
  id: string;
  name: string;
  subdomain: string;
  nip: string | null;
  phone: string | null;
  createdAt: string;
  usersCount: number;
  enabledModules: string[];
  lastActivityAt: string | null;
  /** ISO 8601 timestamp when this tenant was migrated to the new Billing BC. Null if not yet migrated. Derived from legacy via TenantSubscriptionAdminReadPort. */
  migratedAt: string | null;
  /** Inbound/outbound attribution set by admin. Null for tenants created before this feature. */
  clientSource: ClientSource | null;
}

export interface AdminTenantsResponse {
  items: AdminTenantListItem[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Allowed sort columns for the admin tenants list.
 * Mirrors backend `ADMIN_TENANTS_SORT_BY_ALLOWLIST` — keep these in sync.
 */
export type AdminTenantsSortBy = "createdAt" | "name" | "lastActivityAt";
export type AdminTenantsSortOrder = "asc" | "desc";

export interface AdminTenantsQuery {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: AdminTenantsSortBy;
  sortOrder?: AdminTenantsSortOrder;
  clientSource?: ClientSourceFilter;
  marketing?: MarketingFilter;
}

// =====================================================
// Tenant marketing detail (AC-4) — GET /admin/tenants/:id/marketing
// =====================================================

export interface AdminTenantMarketingMember {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  /** Checkbox from registration step 5 (Register.tsx). */
  newsletterConsent: boolean;
  newsletterConsentAt: string | null;
  /** true when this user is suppressed by marketing_opt_out on another org of the same brand. */
  suppressedByOtherOrg: boolean;
}

export interface AdminTenantMarketingDetail {
  /** Meta Pixel cookie consent — NOT email/newsletter consent. Null = no data recorded. */
  marketingConsent: boolean | null;
  members: AdminTenantMarketingMember[];
  marketingOptOut: boolean;
  marketingOptOutNote: string | null;
}

export type DeleteStatus = "PENDING_DELETE" | "DELETED" | "FAILED";
export type SnapshotStatus = "live" | "purged";
export type DeletionType =
  | "manual_admin"
  | "gdpr_erasure"
  | "automatic_cleanup";

export interface AdminTenantDeletionListItem {
  id: string;
  tenantId: string;
  tenantNameSnapshot: string;
  tenantNipSnapshot: string | null;
  deletionType: DeletionType;
  deletedBy: { email: string; name: string };
  deletedAt: string;
  reason: string | null;
  deleteStatus: DeleteStatus;
  snapshotStatus: SnapshotStatus;
  purgeAfter: string;
}

export interface AdminTenantDeletionsResponse {
  items: AdminTenantDeletionListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface AdminTenantDeletionsQuery {
  page?: number;
  limit?: number;
  sortBy?: "deletedAt";
  sortOrder?: "asc" | "desc";
  deleteStatus?: DeleteStatus;
}

export interface AdminTenantDeletionDetail {
  id: string;
  tenantId: string;
  tenantNameSnapshot: string;
  tenantNipSnapshot: string | null;
  deletionType: DeletionType;
  reason: string | null;
  deletedBy: { email: string; name: string };
  deletedAt: string;
  deleteStatus: DeleteStatus;
  snapshotStatus: SnapshotStatus;
  purgeAfter: string;
  schemaVersion?: {
    snapshotSchemaVersion: string;
    lastMigrationName: string;
    lastMigrationTimestamp: string;
    allMigrations?: string[];
    appVersion?: string;
    gitSha?: string;
    capturedAt?: string;
  } | null;
  allMigrationsCount?: number;
  linkedPartyIdsCount?: number;
  failureReason?: string | null;
  retryCount?: number;
  phase2StartedAt?: string | null;
  phase2CompletedAt?: string | null;
}

export interface CreateTenantDeletionPayload {
  tenantId: string;
  reason: string;
  tenantNameConfirmation: string;
}

export interface CreateTenantDeletionResponse {
  archiveId: string;
  deleteStatus: DeleteStatus;
}

// =====================================================
// API calls
// =====================================================

const buildQuery = (q: Record<string, unknown>): string => {
  const params = new URLSearchParams();
  Object.entries(q).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  });
  const s = params.toString();
  return s ? `?${s}` : "";
};

export const getAdminTenants = async (
  query: AdminTenantsQuery = {}
): Promise<AdminTenantsResponse> => {
  const { data } = await apiClient.get<AdminTenantsResponse>(
    `/admin/tenants${buildQuery(query as Record<string, unknown>)}`
  );
  return data;
};

export const getAdminTenantMarketing = async (
  tenantId: string
): Promise<AdminTenantMarketingDetail> => {
  const { data } = await apiClient.get<AdminTenantMarketingDetail>(
    `/admin/tenants/${tenantId}/marketing`
  );
  return data;
};

export const getAdminTenantDeletions = async (
  query: AdminTenantDeletionsQuery = {}
): Promise<AdminTenantDeletionsResponse> => {
  const { data } = await apiClient.get<AdminTenantDeletionsResponse>(
    `/admin/tenant-deletions${buildQuery(query as Record<string, unknown>)}`
  );
  return data;
};

export const getAdminTenantDeletion = async (
  id: string
): Promise<AdminTenantDeletionDetail> => {
  const { data } = await apiClient.get<AdminTenantDeletionDetail>(
    `/admin/tenant-deletions/${id}`
  );
  return data;
};

export const createTenantDeletion = async (
  payload: CreateTenantDeletionPayload
): Promise<CreateTenantDeletionResponse> => {
  const { data } = await apiClient.post<CreateTenantDeletionResponse>(
    "/admin/tenant-deletions",
    payload
  );
  return data;
};
